import { useQuery } from 'convex/react';
import { api } from '@aeon-karaoke/cloud-convex';
import { cn } from '@/lib/cn';

export const AVATAR_ART_BASE = 'https://cdn.aeonkaraoke.com/avatars';

const SIZE = {
  28: 'h-7 w-7 text-[13px]',
  30: 'h-[30px] w-[30px] text-[13px]',
  32: 'h-8 w-8 text-[13px]',
  38: 'h-[38px] w-[38px] text-[15px]',
  56: 'h-14 w-14 text-[22px]',
  88: 'h-[88px] w-[88px] text-[36px]',
  140: 'h-[140px] w-[140px] text-[58px]',
} as const;

/** The current user's row or another person's public summary; both carry
 * these, the summary with nulls where the row has undefined. */
interface AvatarSubject {
  displayName?: string | null;
  email?: string | null;
  imageUrl?: string | null;
  avatarKey?: string | null;
}

interface AvatarDiscProps {
  user: AvatarSubject;
  size: keyof typeof SIZE;
  className?: string;
}

/** Round avatar: a catalog pick first, then the Clerk image, then the initial
 * on gold. A catalog key the list no longer carries falls through. */
export function AvatarDisc({ user, size, className }: AvatarDiscProps) {
  const base = cn(
    'flex shrink-0 items-center justify-center overflow-hidden rounded-full font-semibold',
    SIZE[size],
    className
  );
  const fallback = user.imageUrl ? (
    <span className={base}>
      <img src={user.imageUrl} alt="" className="h-full w-full object-cover" />
    </span>
  ) : (
    <span className={cn(base, 'bg-accFill text-accOn')} aria-hidden="true">
      {(user.displayName ?? user.email ?? '?').charAt(0).toUpperCase()}
    </span>
  );
  if (!user.avatarKey) return fallback;
  return <CatalogAvatar avatarKey={user.avatarKey} className={base} fallback={fallback} />;
}

function CatalogAvatar({ avatarKey, className, fallback }: { avatarKey: string; className: string; fallback: JSX.Element }) {
  const catalog = useQuery(api.avatars.list);
  const picked = catalog?.find((avatar) => avatar.key === avatarKey);
  // The fallback also covers the catalog still loading, so nothing flashes blank.
  if (!picked) return fallback;
  if (picked.illustrated) {
    return (
      <span className={className}>
        <img src={`${AVATAR_ART_BASE}/${picked.key}.png`} alt="" className="h-full w-full object-cover" />
      </span>
    );
  }
  return (
    <span className={className} style={{ background: picked.bg }} aria-hidden="true">
      {picked.emoji}
    </span>
  );
}
